import { useRootStore } from "@/state/RootStore";
import {
  APP_TO_BUILD,
  NUM_SECTIONS,
  PRACTICE_TYPE_DATA,
  PracticeTestType,
  ElectricalUSPracticeTestType,
  SECTION_ID_TO_PRACTICE_TEST_TYPE,
  TEST_TYPE_TO_RESULT_TITLE,
} from "../../../appsConfig";
import moment from "moment";
import { useCallback, useMemo } from "react";
import { localizePracticeTypeData } from "@/i18n/practiceTypeCopy";
import { getCurrentLanguage } from "@/i18n";
import { captureEvent } from "@/utils/posthog";
import * as Sentry from "@sentry/react";
import { getStartTestFailureMessage } from "@/utils/testGenerationErrors";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useToast } from "../ui/use-toast";
import { useToggle } from "react-use";
import { TestMode } from "@/state/TestsStore";
import { useTranslation } from "react-i18next";

import aiChatImage from "@/assets/images/ai-chat-image.png";
import { Lock } from "lucide-react";
import { PracticeSetupDialog } from "./PracticeSetupDialog";
import {
  estimatePracticeDurationMinutes,
  splitDurationMinutes,
} from "@/utils/estimatedDuration";
import { getSinglePracticeLengthTranslationKey } from "@/utils/practiceLength";

const EXAM_SIMULATION = "EXAM_SIMULATION";

interface PracticeItemProps {
  testType: number | string;
  stateSelected: string;
  inTrial: boolean;
  inRow?: boolean;
  coachmarkTarget?: boolean;
}

export const PracticeItem = ({
  testType,
  stateSelected,
  inTrial,
  inRow,
  coachmarkTarget = false,
}: PracticeItemProps) => {
  const store = useRootStore();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { toast } = useToast();

  const { i18n } = useTranslation();
  const currentLanguage = i18n.language || getCurrentLanguage();
  const [allowInstantFeedback, toggleAllowInstantFeedback] = useToggle(true);
  const quickPracticeCoverage = useMemo(
    () => Array.from({ length: NUM_SECTIONS }, (_, index) => {
      const sectionType = SECTION_ID_TO_PRACTICE_TEST_TYPE[index + 1];
      const sectionData = localizePracticeTypeData(
        sectionType,
        PRACTICE_TYPE_DATA[sectionType],
      );
      return sectionData?.resultsTitle || sectionData?.title || TEST_TYPE_TO_RESULT_TITLE[sectionType];
    }).filter((label): label is string => Boolean(label)),
    [currentLanguage],
  );

  const resolvedTestType =
    testType === EXAM_SIMULATION
      ? PracticeTestType.examType
      : (testType as keyof typeof PRACTICE_TYPE_DATA);
  const practiceModeTypeName =
    PracticeTestType.enum[resolvedTestType as keyof typeof PracticeTestType.enum] ??
    String(testType);

  let practiceTypeData = useMemo(
    () =>
      localizePracticeTypeData(
        resolvedTestType,
        PRACTICE_TYPE_DATA[resolvedTestType],
      ),
    [resolvedTestType, currentLanguage],
  );

  const examTestType = testType === EXAM_SIMULATION;
  const mainDescription = examTestType
    ? store.questionsStore.getExamTypeMainDescription(stateSelected)
    : practiceTypeData?.mainDescription;

  const altNumQuestions = examTestType
    ? null
    : practiceTypeData?.altNumQuestions;

  const numPassages = practiceTypeData?.numPassages ?? 0;
  const altNumPassages = practiceTypeData?.altNumPassages ?? 0;
  const altNumRCNonPassageQuestions = practiceTypeData?.altNumRCNonPassageQuestions ?? 0;

  // Handle special case for exam simulation
  if (testType === EXAM_SIMULATION) {
    practiceTypeData = {
      image:
        "https://storage.googleapis.com/tempo-public-images/figma-exports%2Fgithub%7C168204332-1747669389933-node-143%3A4776-1747669389444.png",
      title: t("study.exam_simulation_title"),
      description: t("study.exam_simulation_description"),
    };
  }

  if (testType === PracticeTestType.aiChat) {
    practiceTypeData = {
      title: t("study.ai_tutor_title"),
      description: t("study.ai_tutor_description"),
      image: aiChatImage,
    };
  }

  let description = practiceTypeData?.description;
  if (
    APP_TO_BUILD === "electrical" &&
    testType === PracticeTestType.quickPracticeType
  ) {
    let numQuestions = 0;
    if (stateSelected?.includes("L1") || stateSelected?.includes("L2")) {
      numQuestions = 4;
    } else if (stateSelected?.includes("L3")) {
      numQuestions = 4;
    } else if (stateSelected?.includes("L4")) {
      numQuestions = 5;
    } else {
      numQuestions = 6;
    }

    description = t("study.quick_practice_red_seal_description", { count: numQuestions });
  }

  let locked = false;
  let codebookFreeSession = false;

  let numQuestions = examTestType
    ? store.questionsStore.getNumExamTypeQuestions(stateSelected)
    : practiceTypeData.numQuestions;

  let startButtonText = examTestType
    ? t("study.start_exam_questions", { count: numQuestions })
    : t("study.start_practice_questions", { count: numQuestions });
  let altStartButtonText = "";
  let lockedActionLabel: string | undefined;

  const getPracticeLengthDetail = (count: number) => {
    const durationMinutes = estimatePracticeDurationMinutes(count);
    if (!durationMinutes) {
      return t("study.question_count", { count });
    }

    const { hours, minutes } = splitDurationMinutes(durationMinutes);
    const duration = hours > 0
      ? minutes > 0
        ? t("study.duration_hours_minutes", {
            hours: t("study.duration_hours", { count: hours }),
            minutes: t("study.duration_minutes", { count: minutes }),
          })
        : t("study.duration_hours", { count: hours })
      : t("study.duration_minutes", { count: minutes });

    return t("study.question_time", { duration, questions: count });
  };

  if (altNumQuestions) {
    startButtonText = t("study.regular_questions", {
      count: numQuestions,
      detail: getPracticeLengthDetail(numQuestions),
    });
    altStartButtonText = t("study.short_questions", {
      count: altNumQuestions,
      detail: getPracticeLengthDetail(altNumQuestions),
    });
  }

  if (inTrial && APP_TO_BUILD !== "ibew_aptitude") {
    if (testType === PracticeTestType.quickPracticeType) {
      const allowPractice = !store.testStore.quickPracticeDays.has(
        moment().format("MM/DD/YYYY")
      );
      if (allowPractice) {
        startButtonText = t("study.start_practice_one_free_left");
      } else {
        lockedActionLabel =
          APP_TO_BUILD === "electrical"
            ? t("study.unlock_with_all_access_zero_free")
            : t("study.unlock_with_pro_zero_free");
        locked = true;
      }
    } else if (testType === ElectricalUSPracticeTestType.CODEBOOK_MEMORIZATION) {
      const allowCodebook = !store.testStore.hasUsedCodebookToday;
      if (allowCodebook) {
        startButtonText = t("study.start_practice_one_free_left");
        // Free users get the short (5-question) session
        numQuestions = altNumQuestions ?? numQuestions;
        codebookFreeSession = true;
      } else {
        lockedActionLabel = t("study.unlock_with_pro_zero_free");
        locked = true;
      }
    } else {
      lockedActionLabel =
        APP_TO_BUILD === "electrical"
          ? t("study.unlock_with_all_access")
          : t("study.unlock_with_pro");
      locked = true;
    }
  }

  const handleOnClick = useCallback(() => {
    if (testType === PracticeTestType.aiChat) {
      captureEvent("ai_chat_clicked");
      navigate("/ai-chat");
      return;
    }

    captureEvent("practice_mode_select", {
      practice_mode_type: practiceTypeData.title,
    });
  }, [navigate, testType, practiceTypeData]);

  const handleStartExam = useCallback(
    (alt: boolean) => {
      if (locked) {
        navigate("/paywall");
        return;
      }

      const testMode = examTestType ? TestMode.EXAM : TestMode.PRACTICE;
      const passageCount = alt ? altNumPassages : numPassages;
      const rcNonPassageCount = alt ? altNumRCNonPassageQuestions : 0;
      try {
        store.testStore.startTest(
          testMode,
          testType === EXAM_SIMULATION ? PracticeTestType.examType : testType,
          alt ? altNumQuestions : numQuestions,
          examTestType ? false : allowInstantFeedback,
          passageCount,
          rcNonPassageCount
        );
      } catch (error) {
        captureEvent("practice_mode_start_failed", {
          practice_mode_type: practiceModeTypeName,
          error: error instanceof Error ? error.message : String(error),
        });
        // The PostHog event above is a product metric; this is the one an
        // on-call engineer can find. Test generation fails for content reasons
        // (a state pointed at a codebook year with no tagged questions, a
        // specialty mode thinner than the requested size) that are invisible
        // without the state and size attached.
        Sentry.captureException(error, {
          tags: { feature: "test_generation" },
          extra: {
            test_type: practiceModeTypeName,
            state_code: store.mainAppStore.selectedStateCode,
            num_questions: alt ? altNumQuestions : numQuestions,
          },
        });
        toast({
          title: t("errors.generic_title"),
          description: getStartTestFailureMessage(error, t),
          variant: "destructive",
        });
        return;
      }

      captureEvent("practice_mode_start", {
        practice_mode_type: practiceModeTypeName,
      });

      navigate("/test");
    },
    [
      locked,
      examTestType,
      testType,
      numQuestions,
      allowInstantFeedback,
      altNumQuestions,
      numPassages,
      altNumPassages,
      altNumRCNonPassageQuestions,
      practiceModeTypeName,
      store,
      navigate,
      toast,
      t,
    ]
  );

  const buttonContent = (
    <button
      data-coachmark-target={coachmarkTarget ? String(testType) : undefined}
      onClick={handleOnClick}
      className={cn(
        "dp-card flex w-full text-left transition-[border-color,box-shadow] hover:border-[var(--dp-brand-button)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2",
        inRow
          ? "min-h-[150px] flex-col items-end justify-between gap-4 p-6"
          : "min-h-[140px] flex-col items-start gap-3 p-6",
      )}
      type="button"
    >
      {inRow ? (
        <div className="flex w-full items-start gap-4">
          <img
            src={practiceTypeData?.image}
            alt={t("study.dialog_icon_alt", { title: practiceTypeData.title })}
            className="h-[52px] w-[52px] shrink-0 object-contain"
          />
          <div className="flex min-w-0 flex-1 flex-col">
            <h3 className="text-start text-lg font-semibold leading-[27px] text-[#0a0c11]">
              {practiceTypeData?.title}
            </h3>
            <p className="text-left text-sm font-normal leading-[21px] tracking-[0.07px] text-[#5b616d]">
              {description}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-col gap-3">
          <div className="flex w-full items-center gap-3">
            <img
              src={practiceTypeData?.image}
              alt={t("study.dialog_icon_alt", { title: practiceTypeData.title })}
              className="h-10 w-10 shrink-0 object-contain"
            />
            <h3 className="min-w-0 flex-1 text-start text-base font-semibold leading-6 text-[#0a0c11]">
              {practiceTypeData?.title}
            </h3>
            {testType !== PracticeTestType.aiChat &&
              testType !== PracticeTestType.quickPracticeType &&
              testType !== ElectricalUSPracticeTestType.CODEBOOK_MEMORIZATION &&
              inTrial &&
              APP_TO_BUILD !== "ibew_aptitude" && (
                <Lock aria-hidden="true" className="h-6 w-6 shrink-0 text-[#8c929c]" />
              )}
          </div>
          <p className="w-full text-left text-sm font-normal leading-[21px] tracking-[0.07px] text-[#5b616d]">
            {description}
          </p>
        </div>
      )}
      {inRow && (
        <span className={testType === PracticeTestType.quickPracticeType ? "dp-button-primary h-9 px-4 text-sm" : "dp-button-secondary h-9 px-4 text-sm"}>
          {testType === PracticeTestType.aiChat ? t("study.ask_ai") : t("study.start_practice")}
        </span>
      )}
    </button>
  );

  if (testType === PracticeTestType.aiChat) {
    return buttonContent;
  }

  return (
    <PracticeSetupDialog
      allowInstantFeedback={allowInstantFeedback}
      altStartButtonText={altNumQuestions && !codebookFreeSession ? altStartButtonText : undefined}
      coverageLabels={testType === PracticeTestType.quickPracticeType ? quickPracticeCoverage : undefined}
      description={mainDescription}
      icon={practiceTypeData?.image}
      locked={locked}
      lockedActionLabel={lockedActionLabel}
      onInstantFeedbackChange={toggleAllowInstantFeedback}
      onStart={handleStartExam}
      practiceLengthLabel={getPracticeLengthDetail(numQuestions)}
      showFeedbackToggle={!examTestType}
      singleLengthTitle={t(
        getSinglePracticeLengthTranslationKey({
          isExamSimulation: examTestType,
          isQuickPractice: testType === PracticeTestType.quickPracticeType,
        }),
      )}
      startButtonText={startButtonText}
      title={practiceTypeData.title}
    >
      {buttonContent}
    </PracticeSetupDialog>
  );
};

export default PracticeItem;
